import extend from 'extend';
import Quill from 'quill';
import Delta from 'quill-delta';
import { PastedTextFormatter } from './helpers/pasted-text-formatter.helper';
import { CUSTOM_FONT_STYLE, FONT_CLASS } from './styles/custom-font-style.model';
import { MSOListHelper } from './helpers/mso-list.helper';
import { MSOHelper } from './helpers/mso-helper';

/** HACK this fixes an issue where package builds think that these are node packages to import, rather than quill extensions */

const quimport = Quill.import.bind(Quill);
const CODE_BLOCK = quimport('formats/code');
const MODULE = quimport('core/module');
const PARCHMENT = quimport('parchment');

const CONFIG = {
    scope: PARCHMENT.Scope.BLOCK,
    whitelist: ['right', 'center', 'justify'],
};

const ALIGN_ATTRIBUTE = new PARCHMENT.Attributor.Attribute('align', 'align', CONFIG);
const DIRECTION_ATTRIBUTE = quimport('attributors/attribute/direction');

const ALIGN_STYLE = quimport('attributors/style/align');
const BACKGROUND_STYLE = quimport('attributors/style/background');
const COLOR_STYLE = quimport('attributors/style/color');
const DIRECTION_STYLE = quimport('attributors/style/direction');
const FONT_STYLE = CUSTOM_FONT_STYLE;
Quill.register(FONT_STYLE, true);
Quill.register(FONT_CLASS, true);
const SIZE_STYLE = quimport('attributors/style/size');
/** END HACK */

const DOM_KEY = '__ql-matcher';

// default matchers
const CLIPBOARD_CONFIG = [
    [Node.TEXT_NODE, matchText],
    [Node.TEXT_NODE, matchNewline],
    ['br', matchBreak],
    [Node.ELEMENT_NODE, matchNewline],
    [Node.ELEMENT_NODE, matchBlot],
    [Node.ELEMENT_NODE, matchSpacing],
    [Node.ELEMENT_NODE, matchAttributor],
    [Node.ELEMENT_NODE, matchStyles],
    ['li', matchIndent],
    ['b', matchAlias.bind(matchAlias, 'bold')],
    ['i', matchAlias.bind(matchAlias, 'italic')],
    ['style', matchIgnore]
];

// Adding custom matchers
CLIPBOARD_CONFIG.push(
    [Node.ELEMENT_NODE, matchLineHeightStyle]
);

// Quill attributes that are allowed in pasted content
const ATTRIBUTE_ATTRIBUTORS = [
    ALIGN_ATTRIBUTE,
    DIRECTION_ATTRIBUTE
].reduce(function (memo, attr) {
    memo[attr.keyName] = attr;
    return memo;
}, {});

// Quill styles that are allowed in pasted content
const STYLE_ATTRIBUTORS = [
    ALIGN_STYLE,
    BACKGROUND_STYLE,
    COLOR_STYLE,
    DIRECTION_STYLE,
    SIZE_STYLE,
    FONT_STYLE
].reduce(function (memo, attr) {
    memo[attr.keyName] = attr;
    return memo;
}, {});

/**
 * The Clipboard is a customizable module in quill.js (version - 1.3.6).  It holds logic for handling copied/pasted text in the quill editor.
 *
 * This class has been customized to better handle styles and formats copied from common text editors.
 */
// @dynamic
export class Clipboard extends MODULE {
    static DEFAULTS;

    quill;
    options;

    matchers = [];
    container;
    translationContainer;

    constructor(quill, options) {
        super(quill, options);
        this.quill.root.addEventListener('paste', this.onPaste.bind(this));
        /**
         * Container is a hidden div element that will contain pasted content while its being processed and before it applied to document
         */
        this.container = this.quill.addContainer('ql-clipboard');
        this.container.setAttribute('contenteditable', true);
        this.container.setAttribute('tabindex', -1);
        /**
         * Matchers contain logic to determine if an approved style/format exists within a given html element
         */
        this.matchers = [];

        CLIPBOARD_CONFIG.concat(this.options.matchers).forEach(([selector, matcher]) => {
            if (!options.matchVisual && matcher === matchSpacing) {
                return;
            }
            this.addMatcher(selector, matcher);
        });
    }

    /**
     * Adds a selector & function to be applied to pasted content.
     **/
    addMatcher(selector, matcher) {
        this.matchers.push([selector, matcher]);
    }

    /**
     * Part of original quill.js
     *
     * Converts html string to html and sets it as child to hidden div element. Then traverses the dom
     * applying appropriate formats & styles.
     *
     * @returns Delta - quill object with CRUD operation for changes to document. Formats & styles also described in delta.
     */
    convert(html?) {
        if (typeof html === 'string') {
            this.container.innerHTML = html.replace(/\>\r?\n +\</g, '><'); // Remove spaces between tags

            return this.convert();
        }

        const formats = this.quill.getFormat(this.quill.selection.savedRange.index);

        if (formats[CODE_BLOCK.blotName]) {
            const text = this.container.innerText;
            this.container.innerHTML = '';
            return new Delta().insert(text, {
                [CODE_BLOCK.blotName]: formats[CODE_BLOCK.blotName]
            });
        }
        const [elementMatchers, textMatchers] = this.prepareMatching();

        let delta = traverse(this.container, elementMatchers, textMatchers);
        // Removes trailing newline
        if (deltaEndsWith(delta, '\n') && delta.ops[delta.ops.length - 1].attributes == null) {
            delta = delta.compose(new Delta().retain(delta.length() - 1).delete(1));
        }

        this.container.innerHTML = '';

        return delta;
    }

    /**
     * Waits an animation frame before continuing
     */
    async waitOneAnimationFrame() {
        return new Promise((resolve) => {
            window.requestAnimationFrame(() => resolve(null));
        });
    }

    /**
     * Creates and returns a Delta with formatting parsed from pasted content
     *
     * @returns Delta - quill object with CRUD operation for changes to document
     */
    async getDeltaOfPastedContent(e: ClipboardEvent): Delta {
        const unprocessedHTML = e.clipboardData.getData('text/html');
        const plainText = e.clipboardData.getData('text/plain');

        if (!unprocessedHTML && plainText) {
            this.container.innerHTML = plainText.replace(/\n/g, '<br>');
            return this.convert();
        }

        if (!unprocessedHTML && !plainText) {
            await this.waitOneAnimationFrame();

            return new Delta();
        }

        const pastedDoc: Document = new DOMParser().parseFromString(unprocessedHTML, 'text/html');
        const body: HTMLBodyElement = pastedDoc.querySelector('body');

        this.translationContainer = document.createElement('div');
        this.translationContainer.classList.add('agpr-translationContainer');

        const clipboardDiv = document.querySelector('.ql-clipboard');
        clipboardDiv.appendChild(this.translationContainer);

        if (MSOHelper.isTextFromMSODesktop(unprocessedHTML)) {
            MSOHelper.preprocessContent(pastedDoc);
            MSOListHelper.transformMSOListToHTMLList(body);
        }

        body.childNodes.forEach(node => {
            if (node.nodeType !== Node.ELEMENT_NODE) {
                return node;
            }

            /**
             * Formats nodes by cloning and striping unwanted style values.
             * Beware when debugging values parsed later in the script will not appear in logs
             **/
            const translatedNode = PastedTextFormatter.formatAndWalkNodeTree(<HTMLElement> node);
            if (!translatedNode) {
                return;
            }

            this.translationContainer.appendChild(translatedNode);
        });

        const [elementMatchers, textMatchers] = this.prepareMatchingForTranslationContainer();
        let translationDelta: Delta = traverse(this.translationContainer, elementMatchers, textMatchers);

        if (deltaEndsWith(translationDelta, '\n') && translationDelta.ops[translationDelta.ops.length - 1].attributes == null) {
            translationDelta = translationDelta.compose(new Delta().retain(translationDelta.length() - 1).delete(1));
        }

        return translationDelta;
    }

    /**
     * Handles paste events
     */
    async onPaste(e) {
        if (e.defaultPrevented || !this.quill.isEnabled()) {
            return;
        }

        const range = this.quill.getSelection();
        let delta = new Delta().retain(range.index);
        const scrollTop = this.quill.scrollingContainer.scrollTop;
        this.container.focus();
        this.quill.selection.update(Quill.sources.SILENT);

        const pastedDelta = await this.getDeltaOfPastedContent(e);

        setTimeout(() => {
            delta = delta.concat(pastedDelta).delete(range.length);
            this.quill.updateContents(delta, Quill.sources.USER);
            this.quill.setSelection(delta.length() - range.length, Quill.sources.SILENT);
            this.quill.scrollingContainer.scrollTop = scrollTop;
            this.quill.focus();
            if (this.translationContainer) {
                this.translationContainer.remove();
            }
        }, 1);
    }

    /**
     * Part of original quill.js
     *
     * Returns matching functions to be used on this.container
     */
    prepareMatching() {
        const elementMatchers = [],
            textMatchers = [];
        this.matchers.forEach((pair) => {
            const [selector, matcher] = pair;
            switch (selector) {
                case Node.TEXT_NODE:
                    textMatchers.push(matcher);
                    break;
                case Node.ELEMENT_NODE:
                    elementMatchers.push(matcher);
                    break;
                default:
                    [].forEach.call(this.container.querySelectorAll(selector), (node) => {
                        // TODO use weakmap
                        node[DOM_KEY] = node[DOM_KEY] || [];
                        node[DOM_KEY].push(matcher);
                    });
                    break;
            }
        });

        return [elementMatchers, textMatchers];
    }

    /**
     * Returns matching functions to be used on this.translationContainer
     */
    prepareMatchingForTranslationContainer() {
        const elementMatchers = [],
            textMatchers = [];

        this.matchers.forEach((pair) => {
            const [selector, matcher] = pair;

            switch (selector) {
                case Node.TEXT_NODE:
                    textMatchers.push(matcher);
                    break;
                case Node.ELEMENT_NODE:
                    elementMatchers.push(matcher);
                    break;
                default:
                    [].forEach.call(this.translationContainer.querySelectorAll(selector), (node) => {
                        // TODO use weakmap
                        node[DOM_KEY] = node[DOM_KEY] || [];
                        node[DOM_KEY].push(matcher);
                    });
                    break;
            }
        });
        return [elementMatchers, textMatchers];
    }

    /**
     * Part of original quill.js
     */
    dangerouslyPasteHTML(index, html, source = Quill.sources.API) {
        if (typeof index === 'string') {
            this.quill.setContents(this.convert(index), html);
            this.quill.setSelection(0, Quill.sources.SILENT);
        } else {
            const paste = this.convert(html);
            this.quill.updateContents(new Delta().retain(index).concat(paste), source);
            this.quill.setSelection(index + paste.length(), Quill.sources.SILENT);
        }
    }
}

Clipboard.DEFAULTS = {
    matchers: [],
    matchVisual: true
};

/* eslint-disable */

/**
 * Part of original quill.js
 *
 * Recursively applies functions defined in this.matchers against html element and its children
 *
 * @param node: HTMLElement
 * @param elementMatchers: any[]
 * @param textMatchers: any[]
 * @returns Delta
 */
function traverse(node, elementMatchers, textMatchers) { // Post-order
    if (node.nodeType === node.TEXT_NODE) {
        return textMatchers.reduce(function (delta, matcher) {
            return matcher(node, delta);
        }, new Delta());
    } else if (node.nodeType === node.ELEMENT_NODE) {
        return [].reduce.call(node.childNodes || [], (delta, childNode) => {
            let childrenDelta = traverse(childNode, elementMatchers, textMatchers);
            if (childNode.nodeType === node.ELEMENT_NODE) {
                childrenDelta = elementMatchers.reduce(function (childrenDelta, matcher) {
                    return matcher(childNode, childrenDelta);
                }, childrenDelta);

                childrenDelta = (childNode[DOM_KEY] || []).reduce(function (childrenDelta, matcher) {
                    return matcher(childNode, childrenDelta);
                }, childrenDelta);
            }
            return delta.concat(childrenDelta);
        }, new Delta());
    } else {
        return new Delta();
    }
};

/*
* Part of original quill.js
*/
function deltaEndsWith(delta, text) {
    let endText = '';
    for (let i = delta.ops.length - 1; i >= 0 && endText.length < text.length; --i) {
        const op = delta.ops[i];

        if (typeof op.insert !== 'string') {
            break;
        }
        endText = op.insert + endText;
    }
    return endText.slice(-1 * text.length) === text;
};

/**
 * Part of original quill.js
 *
 * Applies format to provided delta
 */
function applyFormat(delta, format, value? ) {
    if (typeof format === 'object') {
        return Object.keys(format).reduce(function (delta, key) {
            return applyFormat(delta, key, format[key]);
        }, delta);
    } else {
        const delt = delta.reduce(function (delta, op) {
            if (op.attributes && op.attributes[format]) {
                return delta.push(op);
            } else {
                return delta.insert(op.insert, extend({}, {
                    [format]: value
                }, op.attributes));
            }
        }, new Delta());
        return delt;
    }
}

/**
 * Returns computed style of node
 *
 * @returns CSSStyleDeclaration
 */
function computeStyle(node) {
    if (node.nodeType !== Node.ELEMENT_NODE)  {
        return {};
    }
    const DOM_KEY = '__ql-computed-style';

    return node[DOM_KEY] || (node[DOM_KEY] = window.getComputedStyle(node));
};

/**
 * Part of original quill.js
 *
 * @param node: HTMLElement
 * @returns boolean
 */
function isLine(node) {
    if (node.childNodes.length === 0) {
        return false; // Exclude embed blocks
    }
    let style = computeStyle(node);

    return ['block', 'list-item'].indexOf(style.display) > -1;
};

/**
 * Part of original quill.js
 */
function matchAlias(format, node, delta) {
    return applyFormat(delta, format, true);
};

/**
 * Part of original quill.js
 *
 * Identifies styles and attributes defined on the node, and applies them to the delta
 */
function matchAttributor(node, delta) {
    let attributes = PARCHMENT.Attributor.Attribute.keys(node);
    let classes = PARCHMENT.Attributor.Class.keys(node);
    let styles = PARCHMENT.Attributor.Style.keys(node);
    let formats = {};

    // assembles the formats object
    attributes.concat(classes).concat(styles).forEach((name) => {
        let attr: any = PARCHMENT.query(name, PARCHMENT.Scope.ATTRIBUTE);

        if (attr != null) {
            formats[attr.attrName] = attr.value(node);
            if (formats[attr.attrName]) {
                return;
            }
        }
        attr = ATTRIBUTE_ATTRIBUTORS[name];

        if (attr != null && (attr.attrName === name || attr.keyName === name)) {
            formats[attr.attrName] = attr.value(node) || undefined;
        }
        attr = STYLE_ATTRIBUTORS[name];

        if (attr != null && (attr.attrName === name || attr.keyName === name)) {
            attr = STYLE_ATTRIBUTORS[name];
            formats[attr.attrName] = attr.value(node) || undefined;
        }
    });

    if (Object.keys(formats).length > 0) {
        // applies formats to delta
        delta = applyFormat(delta, formats);
    }

    return delta;
};

/**
 * Part of original quill.js
 *
 * Identifies a blot defined on the node, and updates the delta
 */
function matchBlot(node, delta) {
    let match: any = PARCHMENT.query(node);
    if (match == null) {
        return delta;
    }

    if (match.prototype instanceof PARCHMENT.Embed) {
        let embed = {};
        let value = match.value(node);
        if (value != null) {
            embed[match.blotName] = value;
            delta = new Delta().insert(embed, match.formats(node));
        }
    } else if (typeof match.formats === 'function') {
        delta = applyFormat(delta, match.blotName, match.formats(node));
    }

    return delta;
};


/**
 * Part of original quill.js
 *
 * Matches a line break
 */
function matchBreak(node, delta) {
    if (!deltaEndsWith(delta, '\n')) {
        delta.insert('\n');
    }
    return delta;
};

/**
 * Part of original quill.js
 */
function matchIgnore() {
    return new Delta();
}

/**
 * Part of original quill.js
 *
 * Matches an indent in the text
 */
function matchIndent(node, delta) {
    let match: any = PARCHMENT.query(node);

    if (match == null || match.blotName !== 'list-item' || !deltaEndsWith(delta, '\n')) {
        return delta;
    }

    let indent = -1;
    let parent = node.parentNode;

    while (!!parent && !parent?.classList?.contains('ql-clipboard') && !parent?.classList?.contains('agpr-translationContainer')) {
        const parentAttribute: any = PARCHMENT.query(parent) || {};

        if (parentAttribute.blotName === 'list') {
            indent += 1;
        }

        parent = parent.parentNode;
    }

    if (indent <= 0) {
        return delta;
    }

    return delta.compose(new Delta().retain(delta.length() - 1).retain(1, {
        indent: indent
    }));
};


/**
 * Part of original quill.js
 *
 * Matches a new line
 */
function matchNewline(node, delta) {
    if (!deltaEndsWith(delta, '\n')) {
        if (isLine(node) || (delta.length() > 0 && node.nextSibling && isLine(node.nextSibling))) {
            delta.insert('\n');
        } else {
            if (node.tagName === 'P' && node.nextSibling && node.nextSibling.tagName === 'P') {
                delta.insert('\n');
            }
        }
    }

    return delta;
};

/**
 * Part of original quill.js
 */
function matchSpacing(node, delta) {
    if (isLine(node) && node.nextElementSibling != null && !deltaEndsWith(delta, '\n\n')) {
        let nodeHeight = node.offsetHeight + parseFloat(computeStyle(node).marginTop) + parseFloat(computeStyle(node).marginBottom);
        if (node.nextElementSibling.offsetTop > node.offsetTop + nodeHeight * 1.5) {
            delta.insert('\n');
        }
    }

    return delta;
};

/**
 * Part of original quill.js
 *
 * Matches some text styles
 */
function matchStyles(node, delta) {
    let formats: any = {};
    let style = node.style || {};
    if (style.FONT_STYLE && computeStyle(node).FONT_STYLE === 'italic') {
        formats.italic = true;
    }
    if (style.fontWeight && (computeStyle(node).fontWeight.startsWith('bold') ||
            parseInt(computeStyle(node).fontWeight) >= 700)) {
        formats.bold = true;
    }
    if (Object.keys(formats).length > 0) {
        delta = applyFormat(delta, formats);
    }
    if (parseFloat(style.textIndent || 0) > 0) { // Could be 0.5in
        delta = new Delta().insert('\t').concat(delta);
    }

    return delta;
};

/**
 * Part of original quill.js
 *
 * Matches text
 */
function matchText(node, delta) {
    let text = node.data;
    // Word represents empty line with <o:p>&nbsp;</o:p>
    if (node.parentNode.tagName === 'O:P') {
        return delta.insert(text.trim());
    }
    if (text.trim().length === 0 && node.parentNode.classList.contains('ql-clipboard')) {
        return delta;
    }
    if (!computeStyle(node.parentNode).whiteSpace.startsWith('pre')) {
        // eslint-disable-next-line func-style
        let replacer = function (collapse, match) {
            match = match.replace(/[^\u00a0]/g, ''); // \u00a0 is nbsp;
            return match.length < 1 && collapse ? ' ' : match;
        };
        text = text.replace(/\r\n/g, ' ').replace(/\n/g, ' ');
        text = text.replace(/\s\s+/g, replacer.bind(replacer, true)); // collapse whitespace
        if ((node.previousSibling == null && isLine(node.parentNode)) ||
            (node.previousSibling != null && isLine(node.previousSibling))) {
            text = text.replace(/^\s+/, replacer.bind(replacer, false));
        }
        if ((node.nextSibling == null && isLine(node.parentNode)) ||
            (node.nextSibling != null && isLine(node.nextSibling))) {
            text = text.replace(/\s+$/, replacer.bind(replacer, false));
        }
    }

    return delta.insert(text);
};

/* eslint-enable */

//#region  'Custom functions we have added'

function matchParagraphIndentation(node, delta) {
    if (node.getAttribute('style')?.indexOf('mso-list') > -1) {
        return delta;
    }
    const ops = delta.ops.map(op => Object.assign({}, op));
    if (ops[0].insert.startsWith('\t') && node.style.textIndent) {
        if (ops[0].insert === '\t') {
            ops.shift();
        }
        const tabs = Math.ceil(parseInt(node.style.textIndent, 10) / 24);
        for (let i = 0; i < tabs; i++) {
            // convert each tab into 4 white spaces
            ops[0].insert = '\xa0 \xa0 ' + ops[0].insert;
        }
    }

    return new Delta(ops);
};

function matchMsWordWebList(node, delta) {
    if (!(delta?.ops?.length)) {
        return delta;
    }
    delta.ops.forEach(op => {
        if (op.insert === '\n') {
            if (!op.attributes) {
                op.attributes = {};
            }
            op.attributes.indent = parseInt(node.getAttribute('data-aria-level'), 10) - 1;
        }
    });

    return delta;
};

/**
 * Matcher for the custom blot "Line height blot"
 *
 * @returns Delta - with appropriate format value applied
 */
function matchLineHeightStyle(node, delta) {
    const style = node.style || {};
    if (!style || !style.lineHeight || !delta || !delta.ops) {
        return delta;
    }

    delta.ops.forEach(op => {
        if (op?.attributes?.hasOwnProperty('lineheight')) {
            op.attributes.lineheight = style.lineHeight;
        }
    });

    return delta;
};
//#endregion
